import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SearchBoxWithShortcut } from '../../components/SearchBoxWithShortcut'
import { TopRightBar } from '../../components/layout/TopRightBar'
import { FilterButton } from '../../components/buttons/FilterButton'
import { setItemsTextFilter, setDefaultFilterItems } from '../../common/src/actions/filtersActions'
import { getItemFiltersCount, getItemsTextFilter } from '../../common/src/selectors/filtersSelectors'
import { SearchFiltersPanel } from '../filters/SearchFiltersPanel'
import { FILTER_REDUCER_KEYS } from '../../common/src/reducers/filtersKeys'
import { useSearchKeyPress } from '../../hooks/useSearchKeyPress'
import { useTranslation } from 'react-i18next'
import { useFilterTagsFromParams } from '../../hooks/filterHooks'
import { useTextSearch } from '../../hooks/useTextSearch'
import { TopBarMultiSelectButton } from '../../components/buttons/TopBarMultiSelectButton'
import { PrintButton } from '../../components/buttons/PrintButton'

export const SearchTopBar = memo(({ inApp, items, filterType }) => {
  const dispatch = useDispatch()
  const filtersCount = useSelector(state => getItemFiltersCount(state, FILTER_REDUCER_KEYS.SEARCH))
  const clearFilters = () => {
    // DIRECT METHOD - Explicitly clear all filter parameters
    
    // Step 1: Get the current filter type based on the URL path
    let currentFilterType = filterType || FILTER_REDUCER_KEYS.SEARCH;
    
    // If no filterType prop was provided, try to determine from URL
    if (!filterType) {
      const path = window.location.pathname;
      if (path.includes('/matrix/')) {
        currentFilterType = FILTER_REDUCER_KEYS.PROJECT_MATRIX;
      } else if (path.includes('/list/')) {
        currentFilterType = FILTER_REDUCER_KEYS.PROJECT_LIST;
      } else if (path.includes('/kanban/')) {
        currentFilterType = FILTER_REDUCER_KEYS.PROJECT_KANBAN;
      } else if (path.includes('/calendar/')) {
        currentFilterType = FILTER_REDUCER_KEYS.PROJECT_CALENDAR;
      }
    }
    
    // Step 2: Reset Redux state
    dispatch(setDefaultFilterItems(currentFilterType));
    
    // Step 3: Directly clear URL parameters
    try {
      const url = new URL(window.location.href);
      
      // Explicitly list all parameters to clear to ensure we don't miss any
      const paramsToClear = [
        // Search parameters
        'q',
        'item_state',
        'owners',
        
        // IMPORTANT: Both old and new tag parameters
        'tags',
        'item_tags',
        
        // Project related parameters
        'projects',
        'project_ids',
        'exclude_project_ids',
        
        // Other filter parameters
        'quadrant',
        'tag_filter',
        'filter_preset',
        'sort_mode',
        'sort_direction'
      ];
      
      // Log the parameters we're about to clear
      console.log('[DEBUG SearchTopBar] Parameters before clearing:', 
        Object.fromEntries(url.searchParams.entries()));
      
      // Remove each parameter one by one
      paramsToClear.forEach(param => {
        if (url.searchParams.has(param)) {
          console.log(`[DEBUG SearchTopBar] Clearing parameter: ${param}`);
        }
        url.searchParams.delete(param);
      });
      
      // Log the URL after clearing to verify
      console.log('[DEBUG SearchTopBar] URL after clearing filters:', url.toString());
      
      // Update URL without page reload
      window.history.replaceState(null, '', url.toString());
    } catch (error) {
      console.error('Error clearing URL parameters:', error);
    }
  }
  const textFilter = useSelector(state => getItemsTextFilter(state, FILTER_REDUCER_KEYS.SEARCH))
  const setTextFilter = useCallback(
    text => {
      return dispatch(setItemsTextFilter(text, FILTER_REDUCER_KEYS.SEARCH))
    },
    [dispatch]
  )
  const { textSearch, onSearchBoxChange, onSearch } = useTextSearch({ 
    setTextFilter, 
    globalTextSearch: textFilter,
    updateURL: true,
    filterType: FILTER_REDUCER_KEYS.SEARCH 
  })

  const searchBoxRef = useRef(null)
  const [isOpenFiltersPanel, setIsOpenFiltersPanel] = useState(false)
  const { t } = useTranslation()

  const showFiltersPanel = useCallback(() => {
    setIsOpenFiltersPanel(true)
  }, [setIsOpenFiltersPanel])

  const hideFiltersPanel = useCallback(() => {
    setIsOpenFiltersPanel(false)
  }, [setIsOpenFiltersPanel])

  // Focus search box when this view is showed
  useEffect(() => {
    setTimeout(() => {
      searchBoxRef.current?.focus()
    }, 50)
  }, [])

  const onClickSearch = useCallback(() => {
    setTimeout(() => {
      searchBoxRef.current.focus()
    }, 100)
  }, [searchBoxRef])

  useSearchKeyPress(onClickSearch)

  // Apply filter tags from URL parameters
  useFilterTagsFromParams()
  
  // On component mount, check for item_tags parameter to ensure proper handling
  useEffect(() => {
    const url = new URL(window.location.href);
    
    // Only log for debugging, don't modify anything yet
    if (url.searchParams.has('item_tags')) {
      console.log('[DEBUG SearchTopBar] On mount: Found item_tags parameter:', 
        url.searchParams.get('item_tags'));
    }
    
    if (url.searchParams.has('tags')) {
      console.log('[DEBUG SearchTopBar] On mount: Found tags parameter:', 
        url.searchParams.get('tags'));
    }
  }, []);
  const filterButtonRef = useRef(null)

  const rightSubcomponents = (
    <>
      <TopBarMultiSelectButton allItems={items} />
      <SearchBoxWithShortcut
        id="searchTopBar_searchTextField"
        className="max-w-72"
        ref={searchBoxRef}
        value={textSearch}
        placeholder={t('search.top_bar_search_placeholder')}
        onChange={onSearchBoxChange}
        onSearch={onSearch}
      />
      <FilterButton
        id="searchTopBar_filtersButton"
        tooltip={t('search.top_bar_filter_button_tooltip')}
        filtersCount={filtersCount}
        onClick={showFiltersPanel}
        onClear={filtersCount && clearFilters}
        ref={filterButtonRef}
      />
      <PrintButton items={items} className="!hidden md:!flex" />
    </>
  )

  return (
    <>
      <TopRightBar
        className="py-3"
        key={'SearchTopBar'}
        inApp={inApp}
        title={t('search.title')}
        rightSubcomponents={rightSubcomponents}
      />
      <SearchFiltersPanel isOpen={isOpenFiltersPanel} onDismiss={hideFiltersPanel} />
    </>
  )
})
